/**
 * Merges overlapping or touching ranges into non-overlapping ranges.
 *
 * The returned ranges are sorted by start.
 *
 * @param ranges - An array of [start, end] ranges. Each range must have start < end.
 * @returns A new array of non-overlapping, merged ranges sorted by start.
 *
 * @example
 * mergeWindows([[1, 3], [2, 5], [6, 8]]);
 * // returns [[1, 5], [6, 8]]
 */

export const mergeWindows = (ranges: [number, number][]) => {
  if (ranges.length === 0) {
    return [];
  }
  const sortedRange = ranges.slice().sort((a, b) => (a[0] === b[0] ? b[1] - a[1] : a[0] - b[0]));
  const mergedRanges: [number, number][] = [];
  let currentRange = sortedRange[0];
  for (let i = 1; i < sortedRange.length; i++) {
    const [currStart, currEnd] = currentRange;
    const [nextStart, nextEnd] = sortedRange[i];
    if (currEnd >= nextStart) {
      currentRange = [currStart, Math.max(currEnd, nextEnd)];
    } else {
      mergedRanges.push(currentRange);
      currentRange = [...sortedRange[i]];
    }
  }
  mergedRanges.push(currentRange);
  return mergedRanges;
};
/**
 * Subtracts a list of non-overlapping, sorted ranges from a single target range.
 *
 * @param targetWindow - The range to subtract from, e.g., [5, 10]. Must have start < end.
 * @param windowsToSubtract - Ranges to remove from the target. Must be non-overlapping and sorted by start.
 * @returns An array of ranges representing the portions of the target that remain after subtraction.
 *
 * @example
 * subtractRange([5, 10], [[7, 9]]);
 * // returns [[5, 7], [9, 10]]
 */
const subtractRange = (targetWindow: [number, number], windowsToSubtract: [number, number][]): [number, number][] => {
  const [targetStart, targetEnd] = targetWindow;

  const out: [number, number][] = [];
  let cur = targetStart;

  for (const [deleteStart, deleteEnd] of windowsToSubtract) {
    if (deleteEnd <= cur) {
      // ignore delete before target start
      continue;
    }
    if (deleteStart >= targetEnd) {
      // stop computation for deletes after target end
      break;
    }
    if (deleteStart > cur) {
      // delete caused split, add the gap
      out.push([cur, Math.min(deleteStart, targetEnd)]);
    }
    cur = Math.max(cur, deleteEnd);
    if (cur >= targetEnd) {
      // full portion deleted
      return out;
    }
  }

  if (cur < targetEnd) {
    // remaining portion of target
    out.push([cur, targetEnd]);
  }
  return out;
};
/**
 * Subtracts a list of ranges from another list of target ranges.
 *
 * Each range in `toRemove` is merged first to ensure there are no overlaps and then subtracted
 * from each target range in `ranges`.
 *
 * @param ranges - Array of target ranges, e.g., [[5, 10], [15, 20]].
 * @param toRemove - Array of ranges to subtract, e.g., [[7, 9], [18, 19]].
 * @returns Array of ranges representing the portions of `ranges` remaining after subtraction.
 *
 * @example
 * subtractRanges([[5, 10], [15, 20]], [[7, 9], [18, 19]]);
 * // returns [[5, 7], [9, 10], [15, 18], [19, 20]]
 */

export const subtractRanges = (ranges: [number, number][], toRemove: [number, number][]): [number, number][] => {
  const answer: [number, number][] = [];
  const mergedAndSortedRanges = mergeWindows(ranges);
  const mergedAndSortedToRemove = mergeWindows(toRemove);
  for (const range of mergedAndSortedRanges) {
    answer.push(...subtractRange(range, mergedAndSortedToRemove));
  }
  return answer;
};
